"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { motion } from "framer-motion";
import { useGSAP } from "@gsap/react";
import { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useTranslations } from "next-intl";

// // Register ScrollTrigger plugin
// if (typeof window !== "undefined") {
//   gsap.registerPlugin(ScrollTrigger);
// }
//

const MembersSection = () => {
  const pinContainerRef = useRef<HTMLDivElement>(null);
  const horizontalTrackRef = useRef<HTMLDivElement>(null);
  const t = useTranslations("about-us");

  const dataMembers = {
    title: t("members"),
    founders: {
      title: t("founding-team"),
      listFounder: [
        {
          name: t("alex-phan-name"),
          avatar: "/images/about-us/alex-phan.png",
          position: t("alex-phan-position"),
        },
        {
          name: t("jake-nguyen-name"),
          avatar: "/images/about-us/founder_1.png",
          position: t("jake-nguyen-position"),
        },
      ],
    },
    coreTeam: {
      title: t("core-team"),
      listFounder: [],
    },
  };

  useGSAP(
    () => {
      const mm = gsap.matchMedia();

      mm.add("(min-width: 1024px)", () => {
        const horizontalTrack = horizontalTrackRef.current;
        if (!horizontalTrack) return;

        const scrollDistance =
          horizontalTrack.scrollWidth - horizontalTrack.clientWidth;

        const tween = gsap.to(horizontalTrack, {
          x: -scrollDistance,
          ease: "none",
          scrollTrigger: {
            trigger: pinContainerRef.current,
            pin: true,
            scrub: 1,
            start: "top top",
            end: () => `+=${scrollDistance}`,
            invalidateOnRefresh: true,
          },
        });

        return () => {
          tween.kill();
        };
      });
    },
    { scope: pinContainerRef },
  );

  return (
    <div ref={pinContainerRef} className="lg:pt-20">
      <div className="px-6 py-16 md:px-12 lg:flex lg:h-screen lg:flex-col lg:justify-center lg:px-[78px]">
        {/* Title */}
        <motion.h2
          className="text-3xl font-normal text-[#000000] md:text-4xl lg:text-5xl"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          {dataMembers.title}
        </motion.h2>

        <div className="my-10 lg:my-[12px]">
          <div className="my-4 flex flex-shrink-0 flex-col justify-end space-y-3 lg:w-auto">
            <motion.h3
              className="text-base font-normal text-[#0066FF] md:text-lg"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              {dataMembers.founders.title}
            </motion.h3>
            <motion.div
              className="h-4 w-[2px] bg-[#646464]"
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            />
          </div>
          <div
            ref={horizontalTrackRef}
            className="grid grid-cols-2 gap-x-4 gap-y-6 lg:flex lg:flex-nowrap"
          >
            {dataMembers.founders.listFounder.map((member, index) => (
              <motion.div
                key={index}
                className="flex flex-col items-start space-y-4 border-r border-transparent lg:w-[320px] lg:flex-shrink-0 lg:p-3 xl:border-[#E9F3FF]"
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: (index % 2) * 0.1 }}
                viewport={{ once: true }}
              >
                <div
                  className={cn(
                    // index % 2 === 0 ? "lg:h-[452px]" : "lg:h-[348px]",
                    "relative min-h-[200px] w-full lg:h-[452px] overflow-hidden rounded-[24px] sm:min-h-[322px] md:rounded-[32px]",
                  )}
                >
                  <Image
                    src={member.avatar}
                    className="h-full w-full object-contain xl:object-cover"
                    sizes="100vw"
                    width={0}
                    height={0}
                    alt={member.name}
                  />
                </div>
                <div className="space-y-1">
                  <p className="text-base font-normal text-[#202020] lg:text-2xl lg:font-semibold">
                    {member.name}
                  </p>
                  <p className="text-xs font-light text-[#646464] sm:text-sm md:text-base">
                    {member.position}
                  </p>
                </div>
              </motion.div>
            ))}

            <div className="col-span-2 flex w-full flex-shrink-0 flex-col items-start justify-center space-y-3 lg:hidden lg:w-auto">
              <motion.div
                className="h-4 w-[2px] bg-[#646464]"
                initial={{ scaleY: 0 }}
                whileInView={{ scaleY: 1 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              />
              <motion.h3
                className="text-base font-normal text-[#202020] md:text-lg"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                {dataMembers.coreTeam.title}
              </motion.h3>
            </div>

            {dataMembers.coreTeam.listFounder.map((member: any, index) => (
              <motion.div
                key={index}
                className="flex flex-col items-start space-y-4 border-r border-transparent last:border-r-0 lg:w-[320px] lg:flex-shrink-0 lg:p-3 xl:border-[#E9F3FF]"
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: (index % 2) * 0.1 }}
                viewport={{ once: true }}
              >
                <div
                  className={cn(
                    // index % 2 !== 0 ? "lg:h-[452px]" : "lg:h-[348px]",
                    "relative min-h-[322px] lg:h-[452px] w-full overflow-hidden rounded-[24px] max-[450px]:min-h-[163px] md:rounded-[32px]",
                  )}
                >
                  <Image
                    src={member.avatar}
                    className="h-full w-full object-cover"
                    fill
                    alt={member.name}
                  />
                </div>
                <div className="space-y-1">
                  <p className="text-base font-normal text-[#202020] lg:text-2xl lg:font-semibold">
                    {member.name}
                  </p>
                  <p className="text-xs font-light text-[#646464] sm:text-sm md:text-base">
                    {member.position}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="hidden flex-shrink-0 flex-col items-end justify-start space-y-4 lg:flex lg:w-auto lg:items-start">
          <motion.div
            className="h-4 w-[2px] bg-[#646464]"
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          />
          <motion.h3
            className="text-base font-normal text-[#646464] md:text-lg"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Core Team
          </motion.h3>
        </div>
      </div>
    </div>
  );
};

export default MembersSection;
